import * as React from "react";
import { Alert, Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { requestErrorKey, useMyRequests, useWithdrawRequest, type MyRequest } from "@/api/requests";
import { CapabilityCardView } from "@/features/ask/AskResults";
import { RoadmapVoteRow } from "@/features/ask/RequestForm";

function statusLine(t: TFunction, r: MyRequest): string {
  if (r.status === "linked") {
    // No item: it is internal to the platform team, so no FW number.
    return r.item
      ? t("ask.request_status.linked", { fw: r.item.fw })
      : t("ask.request_status.linked_private");
  }
  return t(`ask.request_status.${r.status}`);
}

/** The My requests tab: your requests with their state, and the FW
 *  items you want too. The only place a user sees FW items. */
export function MyRequests(props: { enabled: boolean; onNavigate: () => void }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const mine = useMyRequests(i18n.language, props.enabled);
  const withdraw = useWithdrawRequest();
  const [error, setError] = React.useState<string | null>(null);
  const fail = (err: unknown) => setError(t(requestErrorKey(err) ?? "ask.errors.generic"));

  if (mine.isLoading) return <CircularProgress size={24} sx={{ m: 2 }} />;
  if (mine.isError) return <Alert severity="error">{t("ask.errors.generic")}</Alert>;
  const data = mine.data ?? { requests: [], votes: [] };
  if (data.requests.length === 0 && data.votes.length === 0) {
    return <Typography color="text.secondary">{t("ask.mine_empty")}</Typography>;
  }
  return (
    <Stack sx={{ gap: 2 }}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {data.requests.length > 0 && (
        <Box component="section">
          <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
            {t("ask.mine_requests")}
          </Typography>
          <Stack component="ul" sx={{ listStyle: "none", p: 0, m: 0, gap: 1.5 }}>
            {data.requests.map((r) => (
              <Box component="li" key={r.id} sx={{ borderBottom: 1, borderColor: "divider", pb: 1.5 }}>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                  {r.body}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  <span>{statusLine(t, r)}</span>
                  {r.item ? ` · ${r.item.title} · ${t(`ask.status.${r.item.status}`)}` : ""}
                </Typography>
                {r.decline_reason && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {t("ask.decline_reason", { reason: r.decline_reason })}
                  </Typography>
                )}
                {r.answer && (
                  <Box sx={{ mt: 1 }}>
                    <CapabilityCardView card={r.answer} onNavigate={props.onNavigate} />
                  </Box>
                )}
                {r.status === "new" && (
                  <Button
                    size="small"
                    disabled={withdraw.isPending}
                    onClick={() => withdraw.mutate(r.id, { onError: fail })}
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    {t("ask.withdraw")}
                  </Button>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      )}
      {data.votes.length > 0 && (
        <Box component="section">
          <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
            {t("ask.mine_votes")}
          </Typography>
          <Stack sx={{ gap: 1 }}>
            {data.votes.map((item) => (
              <RoadmapVoteRow key={item.fw} item={item} onError={fail} />
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
